import { Checkbox, Divider, Stack, Typography } from '@mui/material';
import { useCallback } from 'react';
import { useWatch } from 'react-hook-form';

import { useFormContext } from '@/components/react-hook-form';
import { COLORS } from '@/styles/tokens/colors';

import {
  BundlePageItem,
  BundlePageSubItem,
  isContentSubItem,
} from './BundlePageReorderList';

/**
 * Minimal form shape that the sub-item components require.
 */
interface BundlePagesFormSlice {
  pages: BundlePageItem[];
}

interface SubItemRowProps {
  pageIndex: number;
  subIndex: number;
  subItem: BundlePageSubItem;
  /** All sibling sub-items, used to resolve the `dependsOn` sibling's index. */
  allSubItems: BundlePageSubItem[];
}

function SubItemRow({
  pageIndex,
  subIndex,
  subItem,
  allSubItems,
}: SubItemRowProps) {
  const { label, dependsOn } = subItem;
  const { control, setValue, getValues } =
    useFormContext<BundlePagesFormSlice>();
  const checked = useWatch({
    control,
    name: `pages.${pageIndex}.subItems.${subIndex}.checked` as `pages.${number}.checked`,
  });

  // When this row depends on a sibling, it's disabled while that sibling is
  // unchecked. Watch the sibling so the disabled state stays live.
  const dependsOnIndex = dependsOn
    ? allSubItems.findIndex((s) => s.configKey === dependsOn)
    : -1;
  const dependencyChecked = useWatch({
    control,
    name:
      dependsOnIndex >= 0
        ? (`pages.${pageIndex}.subItems.${dependsOnIndex}.checked` as `pages.${number}.checked`)
        : (`pages.${pageIndex}.checked` as `pages.${number}.checked`),
  });
  const isDisabled = dependsOn ? !dependencyChecked : false;

  const handleToggle = useCallback(() => {
    if (isDisabled) return;
    const newChecked = !checked;
    setValue(
      `pages.${pageIndex}.subItems.${subIndex}.checked` as `pages.${number}.checked`,
      newChecked
    );

    // A display option does not decide whether the page has content, so it
    // leaves the parent page.checked unchanged.
    if (!isContentSubItem(subItem)) return;

    // Keep parent page.checked in sync: true if any content sub-item is checked
    if (newChecked) {
      // At least one sub-item is now checked → parent should be checked
      setValue(`pages.${pageIndex}.checked`, true);
    } else {
      // Check if all sibling content sub-items will be unchecked after this
      // toggle. Display options don't keep the parent checked.
      const subItems = getValues(
        `pages.${pageIndex}.subItems` as 'pages.0.subItems'
      );
      const othersChecked = subItems?.some(
        (s, i) => i !== subIndex && isContentSubItem(s) && s?.checked
      );
      if (!othersChecked) {
        setValue(`pages.${pageIndex}.checked`, false);
      }
    }
  }, [setValue, getValues, pageIndex, subIndex, checked, isDisabled, subItem]);

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      pl="64px"
      pr={0.5}
      py="12px"
      sx={{ opacity: isDisabled ? 0.4 : 1 }}
    >
      <Typography
        variant="body-xs"
        sx={{
          flex: 1,
          userSelect: 'none',
          cursor: isDisabled ? 'default' : 'pointer',
        }}
        onClick={handleToggle}
      >
        {label}
      </Typography>
      <Checkbox
        checked={checked}
        onChange={handleToggle}
        size="small"
        disabled={isDisabled}
      />
    </Stack>
  );
}

/**
 * Renders sub-items for a page row as indented checkboxes.
 */
export function SubItemsList({
  pageIndex,
  subItems,
}: {
  pageIndex: number;
  subItems: BundlePageSubItem[];
}) {
  return (
    <Stack
      divider={<Divider sx={{ borderColor: COLORS.GRAY[200], ml: '52px' }} />}
    >
      {subItems.map((subItem, subIndex) => (
        <SubItemRow
          key={subItem.configKey}
          pageIndex={pageIndex}
          subIndex={subIndex}
          subItem={subItem}
          allSubItems={subItems}
        />
      ))}
    </Stack>
  );
}
